<template>
  <div>
    <!-- Overlay mobile -->
    <transition name="overlay-fade">
      <div
        v-if="visivel && isMobile"
        class="drawer-overlay"
        @click="fecharDrawer"
      />
    </transition>

    <transition :name="isMobile ? 'drawer-slide' : ''">
      <div
        v-if="!isMobile || visivel"
        :class="['card-lateral', { 'card-lateral--drawer': isMobile }]"
      >
        <b-card no-body class="card-lateral__card">
          <!-- Botão fechar drawer mobile -->
          <button
            v-if="isMobile"
            class="drawer-close-btn"
            @click="fecharDrawer"
            aria-label="Fechar"
          >
            <i class="feather icon-x" />
          </button>

          <!-- ========== HEADER ========== -->
          <div class="card-header-profile">
            <b-avatar
              size="80px"
              :src="avatarUrl(getNomeCliente())"
              class="card-header-profile__avatar"
            />

            <h5 class="card-header-profile__name">
              {{ getNomeCliente() }}
            </h5>

            <div class="card-header-profile__whatsapp">
              <i class="feather icon-phone" />
              <span>{{ formatarCelular(cliente.cliente.whatsapp) }}</span>
            </div>

            <!-- Status badge -->
            <b-badge
              :variant="statusBadgeVariant"
              pill
              class="card-header-profile__status"
            >
              {{ statusLabel }}
            </b-badge>

            <!-- Etiquetas -->
            <div class="card-header-profile__tags">
              <b-badge
                v-for="tag in cliente.tags"
                :key="tag"
                pill
                class="tag-pill"
                :style="tagStyle(tag)"
                @click="removerTag(tag)"
                title="Clique para remover esta etiqueta"
              >
                <span>{{ tag }}</span>
                <i class="feather icon-x tag-pill__remove" />
              </b-badge>
              <span
                v-if="!cliente.tags || cliente.tags.length === 0"
                class="text-muted small"
              >
                Nenhuma etiqueta
              </span>
            </div>

            <!-- Rating stars -->
            <div
              v-if="cliente.media_score != null"
              class="card-header-profile__rating"
            >
              <span
                v-for="i in 5"
                :key="i"
                class="star"
                :class="{ 'star--filled': i <= Math.round(cliente.media_score) }"
              >★</span>
              <small class="text-muted ml-1">{{ cliente.media_score.toFixed(1) }}</small>
            </div>
          </div>

          <!-- ========== TAB NAVIGATION ========== -->
          <div class="card-body-sections">
            <div class="section-tabs">
              <button
                class="section-tab"
                :class="{ 'section-tab--active': secaoAtiva === 'dados' }"
                @click="selecionarSecao('dados')"
                title="Atendimento"
              >
                <i class="feather icon-headphones" />
                <span class="section-tab__label">Atendimento</span>
              </button>
              <button
                class="section-tab"
                :class="{ 'section-tab--active': secaoAtiva === 'pedidos' }"
                @click="selecionarSecao('pedidos')"
                title="Pedidos"
              >
                <i class="feather icon-package" />
                <span class="section-tab__label">Pedidos</span>
                <b-badge
                  v-if="pedidos.length > 0"
                  pill
                  class="section-tab__badge"
                >
                  {{ pedidos.length }}
                </b-badge>
              </button>
              <button
                class="section-tab"
                :class="{ 'section-tab--active': secaoAtiva === 'historico' }"
                @click="selecionarSecao('historico')"
                title="Histórico"
              >
                <i class="feather icon-clock" />
                <span class="section-tab__label">Histórico</span>
                <b-badge
                  v-if="atendimentosAnteriores.length > 0"
                  pill
                  class="section-tab__badge"
                >
                  {{ atendimentosAnteriores.length }}
                </b-badge>
              </button>
              <button
                class="section-tab"
                :class="{ 'section-tab--active': secaoAtiva === 'atividades' }"
                @click="selecionarSecao('atividades')"
                title="Atividades"
              >
                <i class="feather icon-activity" />
                <span class="section-tab__label">Atividades</span>
              </button>
              <button
                class="section-tab"
                :class="{ 'section-tab--active': secaoAtiva === 'anotacoes' }"
                @click="selecionarSecao('anotacoes')"
                title="Anotações"
              >
                <i class="feather icon-edit" />
                <span class="section-tab__label">Notas</span>
              </button>
            </div>

            <!-- ========== TAB CONTENT ========== -->
            <div class="section-content">
              <!-- 1. Atendimento -->
              <div v-if="secaoAtiva === 'dados'" class="section-panel">
                <div class="info-row info-row--select">
                  <label class="info-row__label" for="tour-selecionar-atendente">Atendente atual</label>
                  <b-form-select
                    id="tour-selecionar-atendente"
                    v-model="cliente.atendenteId"
                    :options="atendentes"
                    :disabled="cliente.atendimento_bot === true"
                    size="sm"
                    class="info-row__select"
                    @change="trocarAtendente"
                  />
                </div>
              </div>

              <!-- 2. Pedidos -->
              <div v-if="secaoAtiva === 'pedidos'" class="section-panel">
                <div v-if="!podeAutoConsultar" class="cpf-input-area">
                  <p class="text-muted small mb-2">
                    Informe o CPF do cliente para buscar os pedidos
                  </p>
                  <div class="cpf-input-row">
                    <b-form-input
                      :value="cpfManual"
                      placeholder="000.000.000-00"
                      size="sm"
                      class="cpf-input"
                      maxlength="14"
                      inputmode="numeric"
                      autocomplete="off"
                      @input="onCpfManualInput"
                      @keyup.enter="submeterCPF"
                    />
                    <b-button
                      size="sm"
                      variant="primary"
                      class="cpf-btn"
                      :disabled="!cpfManualValido || carregandoPedidos"
                      @click="submeterCPF"
                    >
                      <b-spinner v-if="carregandoPedidos" small />
                      <span v-else>Buscar</span>
                    </b-button>
                  </div>
                </div>

                <!-- Loading -->
                <div v-else-if="carregandoPedidos" class="empty-state">
                  <b-spinner small variant="secondary" />
                  <span class="text-muted small mt-1">Buscando pedidos...</span>
                </div>

                <!-- Empty state com opção de vincular CPF -->
                <div v-else-if="pedidos.length === 0 && !carregandoPedidos" class="empty-state-pedidos">
                  <i class="feather icon-inbox empty-state__icon" />
                  <span class="text-muted small">Nenhum pedido encontrado</span>
                  <div
                    v-if="cliente.cliente && cliente.cliente.id"
                    class="cpf-vincular-area"
                  >
                    <p class="text-muted small mb-2">
                      Vincular pedido ou CPF manualmente
                    </p>
                    <b-form @submit.prevent="vincularPedidoManual">
                      <b-form-input
                        v-model="pedidoManualVinculo"
                        placeholder="Ex: 123456 ou 000.000.000-00"
                        size="sm"
                        class="mb-2"
                        autocomplete="off"
                      />
                      <b-button
                        type="submit"
                        size="sm"
                        variant="primary"
                        class="cpf-btn"
                        block
                        :disabled="carregandoPedidos"
                      >
                        <b-spinner
                          v-if="carregandoPedidos"
                          small
                        />
                        <span v-else>Vincular ao cliente</span>
                      </b-button>
                    </b-form>
                  </div>
                  <div v-else-if="!cpfInformado" class="cpf-vincular-area">
                    <p class="text-muted small mb-2">
                      Tente vincular um CPF para buscar pedidos
                    </p>
                    <div class="cpf-input-row">
                      <b-form-input
                        :value="cpfManual"
                        placeholder="000.000.000-00"
                        size="sm"
                        class="cpf-input"
                        maxlength="14"
                        inputmode="numeric"
                        autocomplete="off"
                        @input="onCpfManualInput"
                        @keyup.enter="submeterCPF"
                      />
                      <b-button
                        size="sm"
                        variant="primary"
                        class="cpf-btn"
                        :disabled="!cpfManualValido"
                        @click="submeterCPF"
                      >
                        Vincular
                      </b-button>
                    </div>
                  </div>
                </div>

                <!-- Lista de pedidos -->
                <div v-else class="pedidos-dropdown-list">
                  <PedidoDropdownItem
                    v-for="pedido in pedidos"
                    :key="pedido.id || pedido.numero"
                    :pedido="pedido"
                    :cliente-id="clienteIdParaPedido"
                    :fetch-pedido-detalhe="buscaPedidoDetalheComToken"
                  />
                </div>
              </div>

              <!-- 3. Histórico de Atendimentos -->
              <div v-if="secaoAtiva === 'historico'" class="section-panel">
                <div v-if="atendimentosVisiveis.length === 0" class="empty-state">
                  <i class="feather icon-message-square empty-state__icon" />
                  <span>Primeiro atendimento deste cliente</span>
                </div>
                <div v-else class="historico-cards-scroll">
                  <div
                    v-for="item in atendimentosVisiveis"
                    :key="item.id || item.atendimento_id"
                    class="historico-card"
                    @click="verDetalhes(item.atendimento_id)"
                  >
                    <div class="historico-card__header">
                      <span class="historico-card__date">
                        <i class="feather icon-calendar" />
                        {{ formatDateOnly(item.fim) }}
                      </span>
                      <b-badge
                        v-if="item.status"
                        pill
                        :variant="historicoStatusVariant(item.status)"
                        class="historico-card__status"
                      >
                        {{ item.status }}
                      </b-badge>
                    </div>
                    <div class="historico-card__body">
                      <div class="historico-card__atendente">
                        <i class="feather icon-user" />
                        <span><strong>Resolvido por:</strong> {{ getNomeAtendente(item.atendente_id) }}</span>
                      </div>
                      <div v-if="item.duracao" class="historico-card__duracao">
                        <i class="feather icon-clock" />
                        <span>{{ item.duracao }}</span>
                      </div>
                    </div>
                    <div class="historico-card__footer">
                      <span class="historico-card__link">
                        Ver detalhes
                        <i class="feather icon-chevron-right" />
                      </span>
                    </div>
                  </div>
                </div>
                <b-link
                  v-if="atendimentosAnteriores.length > 5"
                  class="ver-todos-link"
                  @click="$emit('ver-detalhes-atendimento')"
                >
                  Ver todos
                </b-link>
              </div>

              <!-- 4. Atividades (campanhas + notificações) -->
              <div v-if="secaoAtiva === 'atividades'" class="section-panel section-panel--atividades">
                <!-- Campanhas -->
                <div class="atividade-dropdown">
                  <button
                    type="button"
                    class="atividade-dropdown__header"
                    :aria-expanded="dropdownCampanhasAberto"
                    @click="dropdownCampanhasAberto = !dropdownCampanhasAberto"
                  >
                    <span class="atividade-dropdown__title">
                      <i class="feather icon-send" />
                      Campanhas
                      <b-badge
                        v-if="atividadesCarregadas && atividades.length > 0"
                        pill
                        variant="secondary"
                        class="atividade-dropdown__badge"
                      >
                        {{ atividades.length }}
                      </b-badge>
                    </span>
                    <i
                      class="feather icon-chevron-down atividade-dropdown__chevron"
                      :class="{ 'atividade-dropdown__chevron--open': dropdownCampanhasAberto }"
                    />
                  </button>
                  <div v-show="dropdownCampanhasAberto" class="atividade-dropdown__body">
                    <div v-if="carregandoAtividades" class="empty-state empty-state--compact">
                      <b-spinner small variant="secondary" />
                      <span class="text-muted small mt-1">Carregando campanhas...</span>
                    </div>
                    <div v-else-if="erroAtividades" class="empty-state empty-state--compact">
                      <i class="feather icon-alert-circle empty-state__icon text-danger" />
                      <span class="text-muted small">{{ erroAtividades }}</span>
                    </div>
                    <div v-else-if="atividades.length === 0 && atividadesCarregadas" class="empty-state empty-state--compact">
                      <i class="feather icon-send empty-state__icon" />
                      <span class="text-muted small">Nenhuma campanha enviada</span>
                      <span class="text-muted small mt-1" style="font-size: 11px;">Este cliente ainda não recebeu campanhas</span>
                    </div>
                    <div v-else class="atividades-lista">
                      <div
                        v-for="ativ in atividades"
                        :key="ativ.id || `camp-${ativ.nome}-${ativ.data_envio}`"
                        class="atividade-card"
                      >
                        <div class="atividade-card__header">
                          <div class="atividade-card__tipo">
                            <i class="feather icon-send atividade-card__icon" />
                            <span class="atividade-card__tipo-label">Campanha</span>
                          </div>
                          <span class="atividade-card__date">
                            {{ formatarDataAtividade(ativ.data_envio) }}
                          </span>
                        </div>
                        <h6 class="atividade-card__nome">{{ ativ.nome }}</h6>
                        <p v-if="ativ.mensagem_preview" class="atividade-card__preview">
                          "{{ ativ.mensagem_preview }}{{ ativ.mensagem_preview.length >= 100 ? '...' : '' }}"
                        </p>
                        <div class="atividade-card__footer">
                          <span :class="['atividade-card__status', getStatusClass(ativ.status)]">
                            <span class="status-icon">{{ getStatusIcon(ativ.status) }}</span>
                            {{ getStatusLabel(ativ.status) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Notificações -->
                <div class="atividade-dropdown">
                  <button
                    type="button"
                    class="atividade-dropdown__header"
                    :aria-expanded="dropdownNotificacoesAberto"
                    @click="dropdownNotificacoesAberto = !dropdownNotificacoesAberto"
                  >
                    <span class="atividade-dropdown__title">
                      <i class="feather icon-bell" />
                      Notificações
                      <b-badge
                        v-if="notificacoesCarregadas && notificacoesCliente.length > 0"
                        pill
                        variant="secondary"
                        class="atividade-dropdown__badge"
                      >
                        {{ notificacoesCliente.length }}
                      </b-badge>
                    </span>
                    <i
                      class="feather icon-chevron-down atividade-dropdown__chevron"
                      :class="{ 'atividade-dropdown__chevron--open': dropdownNotificacoesAberto }"
                    />
                  </button>
                  <div v-show="dropdownNotificacoesAberto" class="atividade-dropdown__body">
                    <div v-if="carregandoNotificacoes" class="empty-state empty-state--compact">
                      <b-spinner small variant="secondary" />
                      <span class="text-muted small mt-1">Carregando notificações...</span>
                    </div>
                    <div v-else-if="erroNotificacoes" class="empty-state empty-state--compact">
                      <i class="feather icon-alert-circle empty-state__icon text-danger" />
                      <span class="text-muted small">{{ erroNotificacoes }}</span>
                    </div>
                    <div v-else-if="notificacoesCliente.length === 0 && notificacoesCarregadas" class="empty-state empty-state--compact">
                      <i class="feather icon-bell-off empty-state__icon" />
                      <span class="text-muted small">Nenhuma notificação no histórico</span>
                      <span class="text-muted small mt-1" style="font-size: 11px;">Não há registros de notificações para este cliente</span>
                    </div>
                    <div v-else class="atividades-lista">
                      <div
                        v-for="(notif, idx) in notificacoesCliente"
                        :key="notif.id || `notif-${idx}-${dataNotificacao(notif)}`"
                        class="atividade-card"
                      >
                        <div class="atividade-card__header">
                          <div class="atividade-card__tipo">
                            <i class="feather icon-bell atividade-card__icon" />
                            <span class="atividade-card__tipo-label atividade-card__tipo-label--notif">{{ tipoNotificacao(notif) }}</span>
                          </div>
                          <span class="atividade-card__date">
                            {{ formatarDataAtividade(dataNotificacao(notif)) }}
                          </span>
                        </div>
                        <h6 class="atividade-card__nome">{{ tituloNotificacao(notif, idx) }}</h6>
                        <p v-if="previewNotificacao(notif)" class="atividade-card__preview">
                          "{{ truncarPreview(previewNotificacao(notif)) }}"
                        </p>
                        <div v-if="statusNotificacao(notif)" class="atividade-card__footer">
                          <span class="atividade-card__status status--sent">
                            {{ statusNotificacao(notif) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 5. Anotações -->
              <div v-if="secaoAtiva === 'anotacoes'" class="section-panel">
                <div class="anotacao-input-area">
                  <b-form-textarea
                    v-model="anotacao"
                    placeholder="Adicione uma anotação sobre este cliente..."
                    rows="3"
                    size="sm"
                    class="anotacao-textarea"
                    maxlength="250"
                  />
                  <div class="anotacao-footer">
                    <small class="text-muted">{{ anotacao.length }}/250</small>
                    <b-button
                      size="sm"
                      variant="primary"
                      class="btn-salvar-nota"
                      :disabled="!anotacao.trim() || salvandoNota"
                      @click="salvarAnotacao"
                    >
                      <b-spinner v-if="salvandoNota" small />
                      <span v-else>Salvar anotação</span>
                    </b-button>
                  </div>
                </div>

                <div v-if="carregandoNotas" class="empty-state mt-3">
                  <b-spinner small variant="secondary" />
                  <span class="text-muted small mt-1">Carregando anotações...</span>
                </div>

                <div v-else-if="notas.length === 0 && notasCarregadas" class="empty-state mt-3">
                  <i class="feather icon-edit-2 empty-state__icon" />
                  <span class="text-muted small">Nenhuma anotação</span>
                </div>

                <div v-else class="anotacoes-lista mt-2">
                  <div
                    v-for="nota in notas"
                    :key="nota.id"
                    class="anotacao-item"
                  >
                    <template v-if="editandoNotaId === nota.id">
                      <b-form-textarea
                        v-model="editandoNotaTexto"
                        rows="2"
                        size="sm"
                        class="anotacao-textarea"
                        maxlength="250"
                      />
                      <div class="anotacao-edit-actions">
                        <small class="text-muted">{{ editandoNotaTexto.length }}/250</small>
                        <div class="d-flex gap-1">
                          <b-button size="sm" variant="outline-secondary" @click="cancelarEdicao">
                            Cancelar
                          </b-button>
                          <b-button
                            size="sm"
                            variant="primary"
                            class="btn-salvar-nota"
                            :disabled="!editandoNotaTexto.trim()"
                            @click="salvarEdicao"
                          >
                            Salvar
                          </b-button>
                        </div>
                      </div>
                    </template>
                    <template v-else>
                      <div class="anotacao-item__header">
                        <small class="text-muted">{{ formatDate(nota.created_at) }}</small>
                        <div class="anotacao-item__actions">
                          <button class="anotacao-action-btn" title="Editar" @click="iniciarEdicao(nota)">
                            <i class="feather icon-edit-2" />
                          </button>
                          <button class="anotacao-action-btn anotacao-action-btn--danger" title="Excluir" @click="removerNota(nota.id)">
                            <i class="feather icon-trash-2" />
                          </button>
                        </div>
                      </div>
                      <p class="mb-0 small">{{ nota.note }}</p>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-card>

      </div>
    </transition>

    <!-- Modal de confirmação para excluir anotação -->
    <b-modal
      id="modal-confirmar-excluir-nota"
      v-model="modalExcluirNotaVisivel"
      title="Excluir anotação"
      centered
      size="sm"
      ok-variant="danger"
      ok-title="Excluir"
      cancel-title="Cancelar"
      @ok="confirmarExclusaoNota"
      @cancel="cancelarExclusaoNota"
      @hidden="cancelarExclusaoNota"
    >
      <p class="mb-0">Tem certeza que deseja excluir esta anotação?</p>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment'
import 'moment/locale/pt-br'
import {
  buscaListaPedidos,
  buscaPedido,
  vincularCPF,
  buscaNotasCliente,
  criarNota,
  editarNota,
  excluirNota,
  buscaAtividades,
  buscaHistoricoNotificacoesCliente,
} from '@/functions/basics/chat'
import CampoTags from './CampoTags.vue'
import PedidoDropdownItem from './components/PedidoDropdownItem.vue'

function cpfDigitosValido(digits) {
  if (!digits || digits.length !== 11) return false
  if (/^(\d)\1{10}$/.test(digits)) return false
  let sum = 0
  for (let i = 0; i < 9; i += 1) {
    sum += parseInt(digits[i], 10) * (10 - i)
  }
  let rest = sum % 11
  const d1 = rest < 2 ? 0 : 11 - rest
  if (d1 !== parseInt(digits[9], 10)) return false
  sum = 0
  for (let i = 0; i < 10; i += 1) {
    sum += parseInt(digits[i], 10) * (11 - i)
  }
  rest = sum % 11
  const d2 = rest < 2 ? 0 : 11 - rest
  return d2 === parseInt(digits[10], 10)
}

function cpfMascaraVisual(digitosAte11) {
  const d = String(digitosAte11 || '').replace(/\D/g, '').slice(0, 11)
  if (d.length <= 3) return d
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`
}

export default {
  name: 'CardCliente',
  components: {
    CampoTags,
    PedidoDropdownItem,
  },
  props: {
    cliente: Object,
    atendentes: Array,
    tagsDisponiveis: {
      type: Array,
      default: () => [],
    },
    visivel: {
      type: Boolean,
      default: true,
    },
    secaoInicial: {
      type: String,
      default: null,
    },
    storeUuid: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      anotacao: '',
      pedidos: [],
      carregandoPedidos: false,
      pedidoManualVinculo: '',
      cpfManual: '',
      cpfInformado: null,
      secaoAtiva: 'dados',
      windowWidth: window.innerWidth,
      notas: [],
      carregandoNotas: false,
      salvandoNota: false,
      notasCarregadas: false,
      editandoNotaId: null,
      editandoNotaTexto: '',
      modalExcluirNotaVisivel: false,
      notaParaExcluir: null,
      atividades: [],
      carregandoAtividades: false,
      atividadesCarregadas: false,
      erroAtividades: null,
      dropdownCampanhasAberto: true,
      dropdownNotificacoesAberto: true,
      notificacoesCliente: [],
      carregandoNotificacoes: false,
      notificacoesCarregadas: false,
      erroNotificacoes: null,
    }
  },
  computed: {
    isMobile() {
      return this.windowWidth <= 768
    },
    atendimentosAnteriores() {
      return (this.cliente.historico && this.cliente.historico.atendimentos_anteriores) || []
    },
    atendimentosVisiveis() {
      return this.atendimentosAnteriores.slice(0, 5)
    },
    cpfDisponivel() {
      return this.cliente?.cliente?.cpf || null
    },
    clienteIdDisponivel() {
      return this.cliente?.cliente?.id || null
    },
    podeAutoConsultar() {
      return !!(this.cpfDisponivel || this.clienteIdDisponivel || this.cpfInformado)
    },
    clienteIdParaPedido() {
      return this.cpfInformado || this.cpfDisponivel || this.clienteIdDisponivel
    },
    cpfManualDigitos() {
      return (this.cpfManual || '').replace(/\D/g, '')
    },
    cpfManualValido() {
      return cpfDigitosValido(this.cpfManualDigitos)
    },
    clienteIdParaNotas() {
      return this.cliente?.cliente?.id || null
    },
    storeUuidParaAtividades() {
      const routeUuid = this.$route?.params?.uuid
      if (routeUuid) return routeUuid
      return this.storeUuid || this.cliente?.chat_uuid || this.cliente?.store_uuid || this.cliente?.chatUuid || null
    },
    phoneParaAtividades() {
      const whatsapp = this.cliente?.cliente?.whatsapp || this.cliente?.from || ''
      return whatsapp.replace(/\D/g, '')
    },
    chaveClienteAtividades() {
      const cid = this.cliente?.cliente?.id ?? ''
      return `${this.storeUuidParaAtividades || ''}|${this.phoneParaAtividades || ''}|${cid}`
    },
    statusLabel() {
      if (this.cliente.atendimento_bot) return 'IA/Bot'
      if (this.cliente.status === 'resolvido') return 'Resolvido'
      if (this.cliente.status === 'pendente') return 'Pendente'
      return 'Em atendimento'
    },
    statusBadgeVariant() {
      if (this.cliente.atendimento_bot) return 'info'
      if (this.cliente.status === 'resolvido') return 'success'
      if (this.cliente.status === 'pendente') return 'warning'
      return 'primary'
    },
    enderecoFormatado() {
      const c = this.cliente.cliente
      if (!c) return ''
      const parts = [c.endereco, c.cidade, c.estado].filter(Boolean)
      return parts.join(', ')
    },
    dataCadastroFormatada() {
      const d = this.cliente.cliente?.created_at || this.cliente.created_at
      if (!d) return ''
      return this.formatDate(d)
    },
  },
  watch: {
    secaoInicial: {
      immediate: true,
      handler(secao) {
        if (secao) {
          this.secaoAtiva = secao
          this.$emit('secao-consumida')
          if (secao === 'pedidos' && this.podeAutoConsultar && !this.carregandoPedidos) {
            this.buscarPedidos()
          }
        }
      },
    },
    secaoAtiva(nova) {
      if (nova === 'pedidos' && this.pedidos.length === 0 && !this.carregandoPedidos && this.podeAutoConsultar) {
        this.buscarPedidos()
      }
      if (nova === 'anotacoes' && !this.notasCarregadas && !this.carregandoNotas && this.clienteIdParaNotas) {
        this.carregarNotas()
      }
      if (nova === 'atividades') {
        if (!this.atividadesCarregadas && !this.carregandoAtividades) {
          this.carregarAtividades()
        }
        if (!this.notificacoesCarregadas && !this.carregandoNotificacoes) {
          this.carregarNotificacoes()
        }
      }
    },
    chaveClienteAtividades() {
      this.atividades = []
      this.atividadesCarregadas = false
      this.erroAtividades = null
      this.notificacoesCliente = []
      this.notificacoesCarregadas = false
      this.erroNotificacoes = null
      if (this.secaoAtiva === 'atividades') {
        this.carregarAtividades()
        this.carregarNotificacoes()
      }
    },
    clienteIdParaNotas(novoId) {
      if (novoId && this.secaoAtiva === 'anotacoes' && !this.notasCarregadas && !this.carregandoNotas) {
        this.carregarNotas()
      }
    },
  },
  mounted() {
    this._onResize = () => { this.windowWidth = window.innerWidth }
    window.addEventListener('resize', this._onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize)
  },
  methods: {
    buscaPedidoDetalheComToken(clienteId, numeroPedido) {
      const token = this.storeUuidParaAtividades
      if (!token) {
        return Promise.reject(new Error('Token do chat não disponível'))
      }
      return buscaPedido(token, clienteId, numeroPedido)
    },
    async vincularPedidoManual() {
      const raw = (this.pedidoManualVinculo || '').trim()
      if (!raw) {
        this.$bvToast.toast('Por favor, insira um número de pedido ou CPF.', {
          title: 'Campo obrigatório',
          variant: 'warning',
          solid: true,
        })
        return
      }
      if (!this.cliente?.cliente?.id) {
        this.$bvToast.toast('Cliente não encontrado.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
        return
      }
      const chatToken = this.storeUuidParaAtividades
      if (!chatToken) {
        this.$bvToast.toast('Não foi possível identificar o chat da loja.', {
          title: 'Erro',
          variant: 'warning',
          solid: true,
        })
        return
      }
      this.carregandoPedidos = true
      try {
        const resultado = await vincularCPF(chatToken, raw, this.cliente.cliente.id)
        this.pedidos = resultado || []
        this.pedidoManualVinculo = ''
      } catch (err) {
        this.$bvToast.toast('Erro ao vincular pedido ou CPF.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.carregandoPedidos = false
      }
    },
    selecionarSecao(secao) {
      this.secaoAtiva = this.secaoAtiva === secao ? null : secao
    },
    abrirPedidos() {
      this.secaoAtiva = 'pedidos'
    },
    fecharDrawer() {
      this.$emit('fechar')
    },
    getNomeCliente() {
      if (this.cliente?.cliente?.name) return this.cliente.cliente.name
      if (this.cliente?.nome) return this.cliente.nome
      if (this.cliente?.cliente?.whatsapp) return this.formatarCelular(this.cliente.cliente.whatsapp)
      return 'Cliente'
    },
    getNomeAtendente(atendenteId) {
      if (!atendenteId || !this.atendentes) return 'Assistente'
      const atendente = this.atendentes.find(a => a.value === atendenteId || a.id === atendenteId)
      return atendente?.text || atendente?.nome || 'Assistente'
    },
    formatarCelular(numero) {
      if (!numero) return ''
      const limpo = numero.replace(/\D/g, '')
      if (limpo.length === 13) {
        return `+${limpo.slice(0, 2)} (${limpo.slice(2, 4)}) ${limpo.slice(4, 9)}-${limpo.slice(9)}`
      }
      if (limpo.length === 12) {
        return `+${limpo.slice(0, 2)} (${limpo.slice(2, 4)}) ${limpo.slice(4, 8)}-${limpo.slice(8)}`
      }
      return numero
    },
    formatRelativeDate(isoString) {
      return moment(isoString).fromNow()
    },
    formatDate(isoString) {
      if (!isoString) return ''
      const date = new Date(isoString)
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatDateOnly(isoString) {
      if (!isoString) return ''
      const date = new Date(isoString)
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    },
    formatarMoeda(valor) {
      if (valor == null) return 'R$ 0,00'
      return Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    onCpfManualInput(val) {
      const digits = String(val == null ? '' : val).replace(/\D/g, '').slice(0, 11)
      this.cpfManual = cpfMascaraVisual(digits)
    },
    async buscarPedidos() {
      const cpf = this.cpfInformado || this.cpfDisponivel
      if (!cpf && !this.cliente?.cliente?.id) return
      const chatToken = this.storeUuidParaAtividades
      if (!chatToken) {
        this.$bvToast.toast('Não foi possível identificar o chat da loja.', {
          title: 'Aviso',
          variant: 'warning',
          solid: true,
        })
        return
      }
      this.carregandoPedidos = true
      try {
        this.pedidos = await buscaListaPedidos(chatToken, cpf || this.cliente.cliente.id) || []
      } catch (err) {
        this.$bvToast.toast('Erro ao buscar pedidos.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
        this.pedidos = []
      } finally {
        this.carregandoPedidos = false
      }
    },
    submeterCPF() {
      const cpf = this.cpfManualDigitos
      if (!cpfDigitosValido(cpf)) {
        this.$bvToast.toast('Informe um CPF válido (11 dígitos e dígitos verificadores corretos).', {
          title: 'CPF inválido',
          variant: 'warning',
          solid: true,
        })
        return
      }
      this.cpfInformado = cpf
      this.buscarPedidos()
    },
    verDetalhes(atendimentoId) {
      this.$emit('ver-detalhes-atendimento', atendimentoId)
    },
    trocarAtendente(novoId) {
      this.$emit('atualizar-atendente', novoId)
      this.$emit('atualizar-atual-atendente', { atendimentoId: this.cliente.id, novoAtendenteId: novoId })
    },
    avatarUrl(nome) {
      const seed = encodeURIComponent(nome || 'user')
      return `https://api.dicebear.com/7.x/thumbs/svg?seed=${seed}`
    },
    voltarParaChat() {
      this.$emit('voltar-para-chat')
    },
    limparCacheCarrinho() {
      if (this.$refs.modalCarrinho) {
        this.$refs.modalCarrinho.limparCacheCliente()
      }
    },
    tagStyle(tagNome) {
      const tag = this.tagsDisponiveis.find(t => t.nome === tagNome)
      if (tag && tag.cor) {
        return { backgroundColor: tag.cor, color: '#fff' }
      }
      return { backgroundColor: '#9A3092', color: '#fff' }
    },
    removerTag(tagNome) {
      const novasTags = this.cliente.tags.filter(t => t !== tagNome)
      this.$emit('atualizar-tags', novasTags)
    },
    historicoStatusVariant(status) {
      if (!status) return 'secondary'
      const s = status.toLowerCase()
      if (s === 'resolvido' || s === 'resolved') return 'success'
      if (s === 'pendente' || s === 'pending') return 'warning'
      return 'secondary'
    },
    async carregarNotas() {
      if (!this.clienteIdParaNotas) return
      this.carregandoNotas = true
      try {
        this.notas = await buscaNotasCliente(this.clienteIdParaNotas) || []
        this.notasCarregadas = true
      } catch (err) {
        this.$bvToast.toast('Erro ao carregar anotações.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
        this.notas = []
      } finally {
        this.carregandoNotas = false
      }
    },
    async salvarAnotacao() {
      if (!this.anotacao.trim() || this.salvandoNota) return
      if (!this.clienteIdParaNotas) {
        this.$bvToast.toast('Não foi possível identificar o cliente.', {
          title: 'Erro',
          variant: 'warning',
          solid: true,
        })
        return
      }
      this.salvandoNota = true
      try {
        const resultado = await criarNota(this.anotacao.trim(), this.clienteIdParaNotas)
        this.notas.unshift({
          id: resultado.id,
          note: this.anotacao.trim(),
          cliente_id: this.clienteIdParaNotas,
          created_at: new Date().toISOString(),
        })
        this.anotacao = ''
      } catch (err) {
        this.$bvToast.toast('Erro ao salvar anotação.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.salvandoNota = false
      }
    },
    removerNota(id) {
      this.notaParaExcluir = id
      this.modalExcluirNotaVisivel = true
    },
    async confirmarExclusaoNota() {
      if (!this.notaParaExcluir) return
      try {
        await excluirNota(this.notaParaExcluir)
        this.notas = this.notas.filter(n => n.id !== this.notaParaExcluir)
        this.$bvToast.toast('Anotação excluída com sucesso.', {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
        })
      } catch (err) {
        this.$bvToast.toast('Erro ao excluir anotação.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.notaParaExcluir = null
      }
    },
    cancelarExclusaoNota() {
      this.notaParaExcluir = null
    },
    iniciarEdicao(nota) {
      this.editandoNotaId = nota.id
      this.editandoNotaTexto = nota.note
    },
    async salvarEdicao() {
      if (!this.editandoNotaTexto.trim()) return
      try {
        await editarNota(this.editandoNotaId, this.editandoNotaTexto.trim())
        const nota = this.notas.find(n => n.id === this.editandoNotaId)
        if (nota) nota.note = this.editandoNotaTexto.trim()
        this.cancelarEdicao()
      } catch (err) {
        this.$bvToast.toast('Erro ao editar anotação.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }
    },
    cancelarEdicao() {
      this.editandoNotaId = null
      this.editandoNotaTexto = ''
    },
    async carregarAtividades() {
      if (!this.storeUuidParaAtividades || !this.phoneParaAtividades) {
        this.erroAtividades = 'Não foi possível identificar o cliente ou a loja'
        this.atividadesCarregadas = true
        return
      }
      this.carregandoAtividades = true
      this.erroAtividades = null
      try {
        const response = await buscaAtividades(this.storeUuidParaAtividades, this.phoneParaAtividades, 50)
        this.atividades = response?.data?.atividades || []
        this.atividadesCarregadas = true
      } catch (err) {
        console.error('Erro ao carregar atividades:', err)
        this.erroAtividades = 'Erro ao carregar atividades'
        this.atividades = []
      } finally {
        this.carregandoAtividades = false
      }
    },
    async carregarNotificacoes() {
      const chatId = this.storeUuidParaAtividades
      const clientId = this.cliente?.cliente?.id
      if (!chatId || !clientId) {
        this.erroNotificacoes = !clientId
          ? 'Cliente sem ID cadastrado para histórico de notificações'
          : 'Não foi possível identificar a loja (chat)'
        this.notificacoesCliente = []
        this.notificacoesCarregadas = true
        return
      }
      this.carregandoNotificacoes = true
      this.erroNotificacoes = null
      try {
        const response = await buscaHistoricoNotificacoesCliente(chatId, clientId)
        this.notificacoesCliente = this.normalizarListaNotificacoes(response)
        this.notificacoesCarregadas = true
      } catch (err) {
        console.error('Erro ao carregar notificações:', err)
        this.erroNotificacoes = 'Erro ao carregar notificações'
        this.notificacoesCliente = []
      } finally {
        this.carregandoNotificacoes = false
      }
    },
    normalizarListaNotificacoes(payload) {
      if (!payload) return []
      if (Array.isArray(payload)) return payload
      if (Array.isArray(payload.historico)) return payload.historico
      if (payload.data && Array.isArray(payload.data.historico)) return payload.data.historico
      if (Array.isArray(payload.data)) return payload.data
      if (Array.isArray(payload.notifications)) return payload.notifications
      if (Array.isArray(payload.history)) return payload.history
      if (payload.data && Array.isArray(payload.data.notifications)) return payload.data.notifications
      return []
    },
    tipoNotificacao(n) {
      const s = n && n.nome_status != null ? String(n.nome_status).trim() : ''
      return s || 'Notificação'
    },
    tituloNotificacao(n, idx) {
      return n.template_name_meta || n.template_name || n.name || n.title || n.status_label
        || (n.order_id != null ? `Pedido #${n.order_id}` : null)
        || n.nome_status
        || `Notificação ${idx + 1}`
    },
    dataNotificacao(n) {
      return n.created_at || n.sent_at || n.data_envio || n.updated_at || n.data_formatted || n.timestamp
    },
    previewNotificacao(n) {
      const t = n.body || n.message || n.preview || n.text || n.mensagem_preview || ''
      if (typeof t === 'string' && t.trim()) return t
      const partes = []
      if (n.amount != null && String(n.amount).trim() !== '') {
        partes.push(`Valor: R$ ${n.amount}`)
      }
      if (n.abandoned_cart_id != null) {
        partes.push(`Carrinho #${n.abandoned_cart_id}`)
      }
      if (n.cashback_id != null) {
        partes.push(`Cashback #${n.cashback_id}`)
      }
      return partes.join(' · ')
    },
    truncarPreview(texto, max = 100) {
      if (!texto) return ''
      const s = String(texto).trim()
      if (s.length <= max) return s
      return `${s.slice(0, max)}...`
    },
    statusNotificacao(n) {
      if (n.message_id) {
        if (n.send === 2) return 'Pendente envio'
        const lida = n.read_bool === 1 || n.read_bool === true
        const naoLida = n.read_bool === 0 || n.read_bool === false
        if (lida) return 'Enviada · Lida'
        if (naoLida) return 'Enviada · Não lida'
        return 'Enviada'
      }
      if (n.status_label) return n.status_label
      if (n.status_id != null && typeof n.status_id === 'string') return n.status_id
      if (n.status) return String(n.status)
      return ''
    },
    formatarDataAtividade(isoString) {
      if (!isoString) return ''
      const date = new Date(isoString)
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    getStatusIcon(status) {
      const icons = {
        queued: '⏳',
        sent: '✓',
        delivered: '✓✓',
        read: '✓✓',
        failed: '✗',
        blocked: '🚫',
      }
      return icons[status] || '•'
    },
    getStatusLabel(status) {
      const labels = {
        queued: 'Aguardando',
        sent: 'Enviado',
        delivered: 'Entregue',
        read: 'Lido',
        failed: 'Falhou',
        blocked: 'Bloqueado',
      }
      return labels[status] || status
    },
    getStatusClass(status) {
      const classes = {
        queued: 'status--queued',
        sent: 'status--sent',
        delivered: 'status--delivered',
        read: 'status--read',
        failed: 'status--failed',
        blocked: 'status--blocked',
      }
      return classes[status] || ''
    },
  },
}
</script>

<style scoped>
/* ========== LAYOUT ========== */
.card-lateral {
  background-color: #fff;
  width: 100%;
  max-width: 100%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
}

.card-lateral__card {
  display: flex;
  flex-direction: column;
  border: none;
  border-radius: 0;
  overflow: hidden;
  box-shadow: none;
}

/* ========== HEADER PROFILE ========== */
.card-header-profile {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #eee;
  text-align: center;
}

.card-header-profile__avatar {
  margin-bottom: 10px;
  box-shadow: 0 2px 8px rgba(154, 48, 146, 0.15);
}

.card-header-profile__name {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 4px;
  color: #333;
  word-break: break-word;
}

.card-header-profile__whatsapp {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #888;
  font-size: 13px;
  margin-bottom: 8px;
}

.card-header-profile__whatsapp i {
  font-size: 13px;
}

.card-header-profile__status {
  margin-bottom: 10px;
  font-size: 11px;
  letter-spacing: 0.3px;
}

.card-header-profile__tags {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px;
  margin-bottom: 8px;
}

.tag-pill {
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 20px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: opacity 0.2s;
}

.tag-pill:hover {
  opacity: 0.8;
}

.tag-pill__remove {
  font-size: 11px;
  transition: transform 0.2s;
}

.tag-pill:hover .tag-pill__remove {
  transform: scale(1.2);
}

.card-header-profile__rating {
  display: flex;
  align-items: center;
}

.star {
  font-size: 1.1rem;
  color: #ddd;
  margin-right: 1px;
  transition: color 0.2s;
}

.star--filled {
  color: #fbc02d;
}

/* ========== TABS NAVIGATION ========== */
.card-body-sections {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
}

.section-tabs {
  display: flex;
  border-bottom: 2px solid #eee;
  flex-shrink: 0;
}

.section-tab {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 10px 4px 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #999;
  font-size: 14px;
  position: relative;
  transition: color 0.2s, background-color 0.15s;
}

.section-tab:hover {
  color: #9A3092;
  background-color: #faf5fb;
}

.section-tab--active {
  color: #9A3092;
}

.section-tab--active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 2px;
  background-color: #9A3092;
}

.section-tab__label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.section-tab__badge {
  position: absolute;
  top: 4px;
  right: 8px;
  font-size: 9px;
  padding: 2px 5px;
  background-color: #f0e4f0;
  color: #9A3092;
}

.section-tab i {
  font-size: 16px;
}

/* ========== TAB CONTENT ========== */
.section-content {
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
}

.section-content::-webkit-scrollbar {
  width: 4px;
}

.section-content::-webkit-scrollbar-track {
  background: #f5f5f5;
}

.section-content::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 2px;
}

.section-panel {
  padding: 14px 16px;
}

/* ========== INFO ROWS (Dados do Contato) ========== */
.info-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
}

.info-row__label {
  font-size: 11px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.info-row__value {
  font-size: 13px;
  color: #333;
  word-break: break-word;
}

.info-row--select {
  margin-top: 4px;
}

.info-row__select {
  font-size: 13px;
}

/* ========== PEDIDOS ========== */
.pedidos-dropdown-list {
  max-height: 400px;
  overflow-y: auto;
  margin: -14px -16px -14px;
}

.pedidos-dropdown-list::-webkit-scrollbar {
  width: 3px;
}

.pedidos-dropdown-list::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 2px;
}

.empty-state-pedidos {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0 8px;
  gap: 6px;
}

.cpf-vincular-area {
  width: 100%;
  margin-top: 10px;
  padding-top: 12px;
  border-top: 1px solid #eee;
  text-align: center;
}

.cpf-input-area {
  text-align: center;
}

.cpf-input-row {
  display: flex;
  gap: 8px;
}

.cpf-input {
  flex: 1;
  font-size: 13px;
}

.cpf-btn {
  background-color: #9A3092;
  border-color: #9A3092;
  flex-shrink: 0;
}

.cpf-btn:hover {
  background-color: #832980;
  border-color: #832980;
}

/* ========== TIMELINE (Histórico) ========== */
.historico-cards-scroll {
  max-height: 320px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.historico-cards-scroll::-webkit-scrollbar {
  width: 3px;
}

.historico-cards-scroll::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 2px;
}

.historico-card {
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 10px 12px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.historico-card:hover {
  background: #f5f0f7;
  border-color: #e0d4e8;
  box-shadow: 0 2px 6px rgba(154, 48, 146, 0.08);
}

.historico-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.historico-card__date {
  font-size: 11px;
  color: #888;
  display: flex;
  align-items: center;
  gap: 4px;
}

.historico-card__date i {
  font-size: 10px;
}

.historico-card__status {
  font-size: 10px;
  padding: 2px 8px;
}

.historico-card__body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
}

.historico-card__atendente,
.historico-card__duracao {
  font-size: 12px;
  color: #555;
  display: flex;
  align-items: center;
  gap: 6px;
}

.historico-card__atendente i,
.historico-card__duracao i {
  font-size: 11px;
  color: #9A3092;
  width: 14px;
}

.historico-card__footer {
  border-top: 1px solid #eee;
  padding-top: 8px;
  margin-top: 4px;
}

.historico-card__link {
  font-size: 11px;
  color: #9A3092;
  display: flex;
  align-items: center;
  gap: 4px;
}

.historico-card__link i {
  font-size: 10px;
  transition: transform 0.15s ease;
}

.historico-card:hover .historico-card__link i {
  transform: translateX(2px);
}

.badge-count {
  background-color: #f0e4f0;
  color: #9A3092;
  font-size: 10px;
  font-weight: 600;
}

.small-badge {
  font-size: 10px;
}

/* ========== EMPTY STATE ========== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0;
  color: #bbb;
  font-size: 13px;
  gap: 6px;
}

.empty-state__icon {
  font-size: 28px;
  color: #ddd;
}

/* ========== ANOTAÇÕES ========== */
.anotacao-textarea {
  font-size: 13px;
  border-color: #e0e0e0;
  resize: vertical;
}

.anotacao-textarea:focus {
  border-color: #9A3092;
  box-shadow: 0 0 0 2px rgba(154, 48, 146, 0.12);
}

.btn-salvar-nota {
  background-color: #9A3092;
  border-color: #9A3092;
}

.btn-salvar-nota:hover {
  background-color: #832980;
  border-color: #832980;
}

.anotacoes-lista {
  max-height: 350px;
  overflow-y: auto;
}

.anotacoes-lista::-webkit-scrollbar {
  width: 3px;
}

.anotacoes-lista::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 2px;
}

.anotacao-item {
  padding: 6px 0;
  border-bottom: 1px solid #f2f2f2;
}

.anotacao-item:last-child {
  border-bottom: none;
}

.anotacao-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
}

.anotacao-item__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
}

.anotacao-item__actions {
  display: flex;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.15s;
}

.anotacao-item:hover .anotacao-item__actions {
  opacity: 1;
}

.anotacao-action-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 5px;
  border-radius: 4px;
  color: #999;
  font-size: 13px;
  transition: color 0.15s, background-color 0.15s;
}

.anotacao-action-btn:hover {
  color: #9A3092;
  background-color: #faf5fb;
}

.anotacao-action-btn--danger:hover {
  color: #e74c3c;
  background-color: #fdf0ef;
}

.anotacao-edit-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 6px;
}

.anotacao-input-area {
  margin-bottom: 0;
}

/* ========== MOBILE DRAWER ========== */
.card-lateral--drawer {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  height: -webkit-fill-available;
  z-index: 1040;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  overflow-y: auto;
}

.drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1039;
}

.drawer-close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: none;
  background: #f2f2f2;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #666;
  font-size: 16px;
  transition: background 0.15s;
}

.drawer-close-btn:hover {
  background: #e0e0e0;
}

/* Slide animation */
.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: transform 300ms ease;
}

.drawer-slide-enter,
.drawer-slide-leave-to {
  transform: translateX(100%);
}

/* Overlay fade */
.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 300ms ease;
}

.overlay-fade-enter,
.overlay-fade-leave-to {
  opacity: 0;
}

/* ========== DARK MODE ========== */
body.dark-layout .card-lateral {
  background-color: #283046;
}

body.dark-layout .card-lateral__card {
  background-color: #283046;
}

body.dark-layout .card-header-profile {
  border-bottom-color: #3b4253;
}

body.dark-layout .card-header-profile__name {
  color: #d0d2d6;
}

body.dark-layout .section-tabs {
  border-bottom-color: #3b4253;
}

body.dark-layout .section-tab {
  color: #7f8295;
}

body.dark-layout .section-tab:hover {
  color: #c97ec2;
  background-color: #323a52;
}

body.dark-layout .section-tab--active {
  color: #c97ec2;
}

body.dark-layout .section-tab--active::after {
  background-color: #c97ec2;
}

body.dark-layout .section-tab__badge {
  background-color: #3b2647;
  color: #c97ec2;
}

body.dark-layout .info-row__value {
  color: #d0d2d6;
}

body.dark-layout .cpf-input {
  background-color: #283046;
  color: #d0d2d6;
  border-color: #3b4253;
}

body.dark-layout .cpf-vincular-area {
  border-top-color: #3b4253;
}

body.dark-layout .historico-card {
  background: #283046;
  border-color: #3b4253;
}

body.dark-layout .historico-card:hover {
  background: #323a52;
  border-color: #4b3d5a;
}

body.dark-layout .historico-card__date {
  color: #7f8295;
}

body.dark-layout .historico-card__atendente,
body.dark-layout .historico-card__duracao {
  color: #b4b7bd;
}

body.dark-layout .historico-card__atendente i,
body.dark-layout .historico-card__duracao i {
  color: #c97ec2;
}

body.dark-layout .historico-card__footer {
  border-top-color: #3b4253;
}

body.dark-layout .historico-card__link {
  color: #c97ec2;
}

body.dark-layout .badge-count {
  background-color: #3b2647;
  color: #c97ec2;
}

body.dark-layout .drawer-close-btn {
  background: #3b4253;
  color: #d0d2d6;
}

body.dark-layout .drawer-close-btn:hover {
  background: #4b5367;
}

body.dark-layout .anotacao-textarea {
  background-color: #283046;
  color: #d0d2d6;
  border-color: #3b4253;
}

body.dark-layout .anotacao-item {
  border-bottom-color: #3b4253;
}

body.dark-layout .anotacao-action-btn {
  color: #7f8295;
}

body.dark-layout .anotacao-action-btn:hover {
  color: #c97ec2;
  background-color: #323a52;
}

body.dark-layout .anotacao-action-btn--danger:hover {
  color: #e74c3c;
  background-color: #3b2424;
}

/* ========== ATIVIDADES (dropdowns) ========== */
.section-panel--atividades {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.atividade-dropdown {
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.atividade-dropdown__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 12px;
  border: none;
  background: #fafafa;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease;
}

.atividade-dropdown__header:hover {
  background: #f5f0f7;
}

.atividade-dropdown__title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #333;
}

.atividade-dropdown__title .feather {
  font-size: 16px;
  color: #9A3092;
}

.atividade-dropdown__badge {
  font-size: 10px;
  font-weight: 600;
}

.atividade-dropdown__chevron {
  font-size: 18px;
  color: #888;
  transition: transform 0.2s ease;
}

.atividade-dropdown__chevron--open {
  transform: rotate(180deg);
}

.atividade-dropdown__body {
  padding: 10px 12px 12px;
  border-top: 1px solid #eee;
}

.empty-state--compact {
  padding: 16px 8px;
}

.atividade-card__tipo-label--notif {
  background: #e8f4fc;
  color: #2563eb;
}

/* ========== ATIVIDADES ========== */
.atividades-lista {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 400px;
  overflow-y: auto;
}

.atividades-lista::-webkit-scrollbar {
  width: 3px;
}

.atividades-lista::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 2px;
}

.atividade-card {
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 12px;
  transition: all 0.15s ease;
}

.atividade-card:hover {
  background: #f5f0f7;
  border-color: #e0d4e8;
}

.atividade-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.atividade-card__tipo {
  display: flex;
  align-items: center;
  gap: 6px;
}

.atividade-card__icon {
  font-size: 14px;
  color: #9A3092;
}

.atividade-card__tipo-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #9A3092;
  background: #f5e8f4;
  padding: 2px 8px;
  border-radius: 10px;
}

.atividade-card__date {
  font-size: 11px;
  color: #888;
}

.atividade-card__nome {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin: 0 0 6px 0;
}

.atividade-card__preview {
  font-size: 12px;
  color: #666;
  margin: 0 0 8px 0;
  font-style: italic;
  line-height: 1.4;
}

.atividade-card__footer {
  display: flex;
  justify-content: flex-start;
}

.atividade-card__status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 12px;
  background: #f5f5f5;
  color: #666;
}

.status-icon {
  font-size: 10px;
}

.status--queued {
  background: #f5f5f5;
  color: #888;
}

.status--sent {
  background: #f5f5f5;
  color: #888;
}

.status--delivered {
  background: #f5f5f5;
  color: #666;
}

.status--read {
  background: #e8f4fc;
  color: #3B82F6;
}

.status--failed {
  background: #fdf0ef;
  color: #EF4444;
}

.status--blocked {
  background: #fef7ec;
  color: #F59E0B;
}

body.dark-layout .atividade-dropdown {
  border-color: #3b4253;
  background: #283046;
}

body.dark-layout .atividade-dropdown__header {
  background: #323a52;
}

body.dark-layout .atividade-dropdown__header:hover {
  background: #3d4659;
}

body.dark-layout .atividade-dropdown__title {
  color: #d0d2d6;
}

body.dark-layout .atividade-dropdown__title .feather {
  color: #c97ec2;
}

body.dark-layout .atividade-dropdown__chevron {
  color: #7f8295;
}

body.dark-layout .atividade-dropdown__body {
  border-top-color: #3b4253;
}

body.dark-layout .atividade-card__tipo-label--notif {
  background: #1e3a5f;
  color: #60a5fa;
}

/* ========== ATIVIDADES - DARK MODE ========== */
body.dark-layout .atividade-card {
  background: #283046;
  border-color: #3b4253;
}

body.dark-layout .atividade-card:hover {
  background: #323a52;
  border-color: #4b3d5a;
}

body.dark-layout .atividade-card__icon {
  color: #c97ec2;
}

body.dark-layout .atividade-card__tipo-label {
  background: #3b2647;
  color: #c97ec2;
}

body.dark-layout .atividade-card__date {
  color: #7f8295;
}

body.dark-layout .atividade-card__nome {
  color: #d0d2d6;
}

body.dark-layout .atividade-card__preview {
  color: #b4b7bd;
}

body.dark-layout .atividade-card__status {
  background: #3b4253;
  color: #b4b7bd;
}

body.dark-layout .status--read {
  background: #1e3a5f;
  color: #60a5fa;
}

body.dark-layout .status--failed {
  background: #3b2424;
  color: #f87171;
}

body.dark-layout .status--blocked {
  background: #3b3424;
  color: #fbbf24;
}

/* ========== FINANCEIRO ========== */
.financeiro-bloco {
  margin-top: 14px;
  border: 1px solid #e8d8f0;
  border-radius: 8px;
  overflow: hidden;
}

.financeiro-bloco__titulo {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #f5e8f4;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #9A3092;
}

.financeiro-bloco__titulo .feather {
  font-size: 13px;
}

.financeiro-linha {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 12px;
  border-top: 1px solid #f0e0f8;
  gap: 8px;
}

.financeiro-linha__nome {
  font-size: 12px;
  color: #555;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.financeiro-linha__valores {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.financeiro-linha__pedidos {
  font-size: 11px;
  color: #999;
}

.financeiro-linha__valor {
  font-size: 12px;
  font-weight: 600;
  color: #333;
}

.financeiro-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-top: 2px solid #e0c8ec;
  background: #faf5fd;
  gap: 8px;
}

.financeiro-total__label {
  font-size: 12px;
  font-weight: 700;
  color: #9A3092;
}

.financeiro-total__valores {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.financeiro-total__pedidos {
  font-size: 11px;
  color: #999;
}

.financeiro-total__valor {
  font-size: 13px;
  font-weight: 700;
  color: #9A3092;
}

/* Dark mode */
body.dark-layout .financeiro-bloco {
  border-color: #4b3d5a;
}

body.dark-layout .financeiro-bloco__titulo {
  background: #3b2647;
  color: #c97ec2;
}

body.dark-layout .financeiro-linha {
  border-color: #3b2647;
}

body.dark-layout .financeiro-linha__nome {
  color: #b4b7bd;
}

body.dark-layout .financeiro-linha__valor {
  color: #d0d2d6;
}

body.dark-layout .financeiro-total {
  background: #2d1f38;
  border-color: #5a3d6e;
}

body.dark-layout .financeiro-total__label,
body.dark-layout .financeiro-total__valor {
  color: #c97ec2;
}
</style>
